import React from 'react';
import { createContext, useContext, useState } from "react";
import { doc, getDoc, setDoc, collection, getDocs, deleteDoc } from "firebase/firestore";
import { db } from "./Firebase.jsx";
import { useAuth } from './AuthContext.jsx';
import { useCounter } from '@MyLib/System/useCounter.js';

const FirestoreContext = createContext(null);

export const FirestoreProvider = ({ children }) => {
	const running = useCounter();
	const {user} = useAuth();

	const saveStore = async (data, path) => {
		running.inc();

		try {
			const ref = doc(db, ...path);
			await setDoc(ref, data);
			//await setDoc(ref, data, { merge: true });
		} catch (err) {
			console.error(err);
		}
		finally {
			running.dec();
		}
	};

	const loadStore = async (path) => {
		running.inc();

		let res = {};
		try {
			const ref = doc(db, ...path);
			const snap = await getDoc(ref);
			if (snap.exists()) {
				res = { status: 200, data: snap.data() };
			}
			else {
				res = { status: 200, data: null };
			}
		} catch (err) {
			console.error(err);
			res = { status: 500 };
		}
		finally {
			running.dec();
		}
		
		return res;
	};

	const deleteStore = async (path) => {
		running.inc();

		try {
			const ref = doc(db, ...path);
			await deleteDoc(ref);
		} catch (err) {
			console.error(err);
		}
		finally {
			running.dec();
		}
	};

	const deleteStoreCollection = async (path) => {
		const saveCollection = collection(db, ...path);
		const saveDocs = await getDocs(saveCollection);

		await Promise.all(saveDocs.docs.map(d => {
			deleteStore([...path, d.id]);
		}));
	};

	const deleteAll = async () => {
		running.inc();

		try {
			const uid = user.uid;
			await deleteStoreCollection(["users", uid, "SaveData", "Deck", "List"]);
			await deleteStoreCollection(["users", uid, "SaveData"]);
			await deleteStore(["users", uid]);
		} catch (err) {
			console.error(err);
		}
		finally {
			running.dec();
		}
	};

	return (
		<FirestoreContext.Provider value={{
			running: running.active,

			deleteAll,

			saveDeckField: async (data) => {
				if (user) {
					await saveStore(data, ["users", user.uid, "SaveData", "Deck"]);
				}
			},
			loadDeckField: async () => {
				if (user) {
					return await loadStore(["users", user.uid, "SaveData", "Deck"]);
				}
				return { status: 500 };
			},

			saveDeck: async (deck) => {
				if (user) {
					await saveStore(deck, ["users", user.uid, "SaveData", "Deck", "List", deck.id]);
				}
			},
			loadDeck: async (deckId) => {
				if (user) {
					return await loadStore(["users", user.uid, "SaveData", "Deck", "List", deckId]);
				}
				return { status: 500 };
			},
			deleteDeck: async (deckId) => {
				if (user) {
					await deleteStore(["users", user.uid, "SaveData", "Deck", "List", deckId]);
				}
			}
		}}>
			{children}
		</FirestoreContext.Provider>
	);
};

export const useFirestore = () => useContext(FirestoreContext);
